import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  bulkUpdateTasks,
  createTask,
  deleteTask,
  getEstimateTotals,
  getPersonalProject,
  getGeneralProject,
  getTask,
  listTasks,
  updateTask,
  type BulkAction,
  type CreateTaskInput,
  type EstimateTotalsFilters,
  type TaskFilters,
  type UpdateTaskInput,
  duplicateTask,
  type DuplicateTaskInput,
} from '../lib/tasksApi';

export function useTasks(filters: TaskFilters = {}) {
  return useQuery({
    queryKey: ['tasks', filters],
    queryFn: () => listTasks(filters),
  });
}

// CLE-435: the totals reflect the active filters, so the header recomputes when
// a responsible/status/priority/etc. is selected (the filters are part of the
// query key so the aggregate refetches on any change).
export function useEstimateTotals(filters: EstimateTotalsFilters = {}) {
  return useQuery({
    queryKey: ['task-estimate-totals', filters],
    queryFn: () => getEstimateTotals(filters),
  });
}

// The caller's hidden personal-tasks project (lazily created server-side).
// Immutable per session, hence staleTime: Infinity.
export function usePersonalProject() {
  return useQuery({
    queryKey: ['personal-project'],
    queryFn: () => getPersonalProject(),
    staleTime: Infinity,
  });
}

// The company's "Fără proiect" container (tasks without a project), lazily
// created server-side. Never changes once created, hence staleTime: Infinity.
export function useGeneralProject() {
  return useQuery({
    queryKey: ['general-project'],
    queryFn: () => getGeneralProject(),
    staleTime: Infinity,
  });
}

export function useTask(id: string | undefined) {
  return useQuery({
    queryKey: ['task', id],
    queryFn: () => getTask(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTaskInput) => createTask(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      // A sub-issue is created with a parentId, and the parent's "Sub-sarcini"
      // list is its own query: without this it kept showing the old set, so the
      // same sub-task got added again and again because it never appeared.
      void qc.invalidateQueries({ queryKey: ['task-subtasks'] });
    },
  });
}

/** Duplicate a task (same or another project); the lists pick up the copy. */
export function useDuplicateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: DuplicateTaskInput }) =>
      duplicateTask(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      void qc.invalidateQueries({ queryKey: ['task-subtasks'] });
    },
  });
}

export function useUpdateTask(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateTaskInput) => updateTask(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      void qc.invalidateQueries({ queryKey: ['task', id] });
    },
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteTask(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      // Deleting a task cascades away its timers — refetch so the global bar
      // does not keep counting on a row that no longer exists.
      void qc.invalidateQueries({ queryKey: ['task-timers'] });
      // …and off the parent's sub-issue list, which otherwise kept the deleted
      // row on screen right after the "deleted" confirmation.
      void qc.invalidateQueries({ queryKey: ['task-subtasks'] });
    },
  });
}

// Generic update keyed by the task id passed at call time — used by the Kanban
// board where any card can move, so a per-id hook would not fit.
export function useUpdateTaskById() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string } & UpdateTaskInput) => {
      const { id, ...rest } = input;
      return updateTask(id, rest);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      void qc.invalidateQueries({ queryKey: ['task-estimate-totals'] });
    },
  });
}

export function useBulkUpdateTasks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { ids: string[]; action: BulkAction; value?: unknown }) =>
      bulkUpdateTasks(input.ids, input.action, input.value),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      void qc.invalidateQueries({ queryKey: ['task-estimate-totals'] });
    },
  });
}
